import type { OrderConfirmation } from './api.ts';

/**
 * Warum „AB erneut senden“ gerade nicht geht (epic-auftragseingang Story 11) – ein kurzer Satz unter dem gesperrten
 * Menüpunkt; `null` = senden möglich. Gleiche Bedingungen wie der Server (`orders.sendOrderConfirmation`): nur die
 * neueste, versiegelte, nicht entfernte Fassung mit PDF im Archiv und eine AB-Adresse beim Kunden.
 */
export function confirmationSendBlockedReason(c: OrderConfirmation): string | null {
  const doc = c.document;
  if (!doc) return 'Erst möglich, wenn die Auftragsbestätigung entstanden ist.';
  if (doc.status === 'rendering') return 'Das PDF wird noch erstellt – erst danach senden.';
  if (doc.status === 'invalid') return 'Die Fassung ist ungültig (PDF nicht erzeugt) – so nicht sendbar.';
  if (doc.purged) return 'Nach Fristende entfernt – nicht mehr sendbar.';
  if (!doc.pdfAvailable) return 'Kein PDF im Archiv – so nicht sendbar.';
  if (!c.recipient) return 'Beim Kunden ist keine E-Mail-Adresse für Auftragsbestätigungen hinterlegt.';
  return null;
}

/**
 * Idempotency-Key einer Bestätigung (AD-4): ein Schlüssel je „Bestätigen“-Vorgang, bei jeder Wiederholung derselbe – ging
 * die Antwort verloren, obwohl der Server die E-Mail schon eingestellt hat, liefert er beim zweiten Klick die gespeicherte
 * Antwort statt einer zweiten E-Mail. Neu erst nach Erfolg, Abbrechen oder einer endgültigen Ablehnung (4xx; die
 * speichert der Server zum Schlüssel, eine Wiederholung bekäme sie sonst immer wieder). Behalten bei Netzfehler (0),
 * Serverfehler (ab 500, nicht gespeichert) und „erster Aufruf läuft noch“.
 */
export class RetrySafeKey {
  private key: string | null = null;

  constructor(private readonly create: () => string) {}

  /** Schlüssel des laufenden Vorgangs (beim ersten Aufruf neu). */
  current(): string {
    this.key ??= this.create();
    return this.key;
  }

  /** Vorgang beendet (Erfolg oder Abbrechen): der nächste bekommt einen neuen Schlüssel. */
  reset(): void {
    this.key = null;
  }

  /** Nach einem Fehler: Schlüssel behalten, wenn die Wiederholung dieselbe Anfrage ist; sonst verwerfen. */
  failed(status: number, code: string | null): void {
    const retrySame = status === 0 || status >= 500 || code === 'idempotency_in_progress';
    if (!retrySame) this.reset();
  }
}
